import { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import type { Retrospectives } from "../data/types";
import { fetchRetrospectives } from "../data/retrospectives";
import EditionBlock from "../components/EditionBlock";

export default function Retrospective() {
  const [retrospectives, setRetrospectives] = useState<Retrospectives[]>([]);
  useEffect(() => {
    fetchRetrospectives().then(setRetrospectives);
  }, []);

  const [openEditions, setOpenEditions] = useState<boolean[]>(
    retrospectives.map(() => false)
  );
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pointRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [pathD, setPathD] = useState("");
  const [animateKey, setAnimateKey] = useState(0);

  const toggleOpen = (index: number) => {
    setOpenEditions((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const updatePath = useCallback(() => {
    const points: { x: number; y: number }[] = [];
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const yearPoints: { x: number; y: number }[] = [];

    pointRefs.current.forEach((p, idx) => {
      const isEven = idx % 2 === 0;
      const block = blockRefs.current[idx];
      if (!p || !block) return;

      const rectPoint = p.getBoundingClientRect();
      const rectBlock = block.getBoundingClientRect();

      const yearX = rectPoint.left + rectPoint.width / 2;
      const yearY = rectPoint.top + scrollTop + rectPoint.height / 2;
      yearPoints.push({ x: yearX, y: yearY });

      // Coin du haut du bloc (entrée)
      if (idx !== 0) {
        const entryX = isEven ? rectBlock.left - 10 : rectBlock.right + 10;
        const entryY = rectBlock.top + scrollTop - 10;
        points.push({ x: entryX, y: entryY });
      }

      // Point de l'année
      points.push({ x: yearX, y: yearY });

      // Coin bas du bloc (sortie)
      if (idx !== blockRefs.current.length - 1) {
        const exitX = isEven ? rectBlock.left - 10 : rectBlock.right + 10;
        const exitY = rectBlock.bottom + scrollTop + 10;
        points.push({ x: exitX, y: exitY });
      }
    });

    if (yearPoints.length === 0) return;
    points.unshift({ x: yearPoints[0].x, y: 0 });
    points.push({ x: yearPoints[yearPoints.length - 1].x, y: document.body.scrollHeight });

    if (points.length < 2) return;

    const catmullRomToBezier = (pts: { x: number; y: number }[]) => {
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;

        const x1 = p1.x + (p2.x - p0.x) / 6;
        const y1 = p1.y + (p2.y - p0.y) / 6;
        const x2 = p2.x - (p3.x - p1.x) / 6;
        const y2 = p2.y - (p3.y - p1.y) / 6;

        d += ` C ${x1} ${y1}, ${x2} ${y2}, ${p2.x} ${p2.y}`;
      }
      return d;
    };

    setPathD(catmullRomToBezier(points));
  }, []);

  useEffect(() => {
    if (retrospectives.length > 0) {
      setOpenEditions(retrospectives.map(() => false)); // réinitialise correctement
      // attendre 1 frame pour que les EditionBlock soient dessinés :
      requestAnimationFrame(() => updatePath());
    }
  }, [retrospectives, updatePath]);

  // Calcul initial + mise à jour au resize
  useLayoutEffect(() => {
    updatePath();
    window.addEventListener("resize", updatePath);
    return () => window.removeEventListener("resize", updatePath);
  }, [updatePath]);

  // Observer la taille des blocs
  useEffect(() => {
    const observers: ResizeObserver[] = [];
    blockRefs.current.forEach((block) => {
      if (!block) return;
      const observer = new ResizeObserver(updatePath);
      observer.observe(block);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [openEditions, updatePath]);

  // Réanimer le path toutes les 10s
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimateKey((k) => k + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-12 px-4 md:px-20 bg-bleuFonce">
      <h1 className="text-5xl font-bebas font-bold text-center mb-12 mt-8 text-rouge">
        Rétrospective
      </h1>

      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <motion.path
          key={animateKey} // force la réanimation
          d={pathD}
          fill="none"
          stroke="#FAE810"
          strokeWidth={4}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 5, ease: "easeInOut" }}
        />
      </svg>

      <div className="relative z-10">
        {retrospectives.map((edition, idx) => (
          <EditionBlock
            key={idx}
            edition={edition}
            index={idx}
            isOpen={openEditions[idx]}
            toggleOpen={() => toggleOpen(idx)}
            ref={(el) => {
              blockRefs.current[idx] = el;
              pointRefs.current[idx] = el?.previousElementSibling as HTMLDivElement;
            }}
          />
        ))}
      </div>
    </section>
  );
}
